import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useLocation } from "react-router-dom";
import { ADSENSE_CLIENT, ADSENSE_SLOTS, pushAd } from "../lib/adsense";

/**
 * Fixed skyscraper ad rails on very-wide screens (≥1440px).
 * Reserved slots for AdSense — plug in `<ins class="adsbygoogle" ...>` when Chris has AdSense approval.
 * Hidden for Pro users. Dismissible by anyone.
 */
export function AdRail({ side = "left" }) {
    const { user } = useAuth();
    const { pathname } = useLocation();
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        setDismissed(sessionStorage.getItem("rc_ads_dismissed") === "1");
    }, []);

    // Push an AdSense impression once the slot mounts (only if configured)
    const slotId = ADSENSE_SLOTS[side];
    useEffect(() => {
        if (ADSENSE_CLIENT && slotId && !dismissed && !user?.is_pro) {
            pushAd();
        }
    }, [slotId, dismissed, user?.is_pro]);

    if (user?.is_pro) return null;
    if (dismissed) return null;
    // Don't show on onboarding / studio / auth / payment pages
    const hiddenPaths = ["/themes", "/login", "/signup", "/donate/success"];
    if (hiddenPaths.some((p) => pathname.startsWith(p))) return null;

    const dismiss = () => {
        sessionStorage.setItem("rc_ads_dismissed", "1");
        setDismissed(true);
    };

    return (
        <aside
            data-testid={`ad-rail-${side}`}
            className={`hidden 2xl:flex fixed top-24 bottom-16 z-30 pointer-events-none ${side}-4 flex-col items-center gap-4`}
            style={{ [side]: 16 }}
        >
            <div className="pointer-events-auto w-[160px] card-surface rounded-2xl overflow-hidden flex flex-col">
                <div className="flex items-center justify-between px-3 py-2 border-b border-white/5 text-[10px] uppercase tracking-widest text-muted-foreground">
                    <span>Sponsored</span>
                    <button
                        onClick={dismiss}
                        className="hover:text-accent transition-colors"
                        aria-label="Hide ads"
                        data-testid={`ad-rail-dismiss-${side}`}
                    >
                        <X className="h-3 w-3" />
                    </button>
                </div>
                <div
                    className="flex-1 min-h-[600px] flex items-center justify-center text-center px-4 py-6"
                    id={`adsbygoogle-slot-${side}`}
                    data-testid={`ad-slot-${side}`}
                >
                    {ADSENSE_CLIENT && slotId ? (
                        <ins
                            className="adsbygoogle"
                            style={{ display: "block", width: "160px", height: "600px" }}
                            data-ad-client={ADSENSE_CLIENT}
                            data-ad-slot={slotId}
                            data-ad-format="vertical"
                            data-full-width-responsive="false"
                            data-testid={`adsense-${side}`}
                        />
                    ) : (
                        <div className="text-muted-foreground/70 text-xs leading-relaxed">
                            Your ad here.<br />
                            <span className="text-primary">Support the mission</span>
                            <br />
                            <a
                                href="/pricing"
                                className="mt-3 inline-block text-[11px] text-accent underline underline-offset-4 opacity-70 hover:opacity-100"
                            >
                                Go ad-free → Pro
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </aside>
    );
}
